/**
 * AIGate — how the web app honours the AI master switch (hub standard §5b).
 *
 * useAI() reads the switch from /auth/me ({ai_enabled, ai_off_display}). While
 * AI is on every control works as normal. While it is off each AI control either
 * does not render (mode 'hidden') or renders visibly disabled beside an
 * AskEnableAI link (mode 'disabled_request') that files a normal change request
 * to the support desk. The server never calls a provider while off, so this is
 * the presentation half of the same switch.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { tokens } from '../api.js';
import { useAuth } from '../App.jsx';

/** {on, hidden, disabled} for the current account; defaults to on when unknown. */
export function useAI() {
  const { me } = useAuth() || {};
  const on = me?.ai_enabled !== false;
  const mode = me?.ai_off_display || 'disabled_request';
  return { on, hidden: !on && mode === 'hidden', disabled: !on && mode !== 'hidden' };
}

/** "Ask about enabling AI" — files a change request (kind=change) through the
 * same journeys endpoint the Report button uses, so it lands in the Support
 * inbox and notifies the owner. Shows its own sent / failed line. */
export function AskEnableAI({ feature = 'this feature', className = '' }) {
  const [state, setState] = useState('idle');   // idle | sending | sent | failed
  const send = async () => {
    if (state === 'sending' || state === 'sent') return;
    setState('sending');
    try {
      const fd = new FormData();
      fd.append('kind', 'change');
      fd.append('capture', 'dom');
      fd.append('surface', 'frontend');
      fd.append('page_url', window.location.href);
      fd.append('title', 'Ask about enabling AI');
      fd.append('description', `Please enable AI in Vault — I tried to use ${feature}.`);
      const res = await fetch('/api/feedback/journeys/', {
        method: 'POST',
        headers: tokens.access ? { Authorization: `Bearer ${tokens.access}` } : {},
        body: fd,
      });
      setState(res.ok ? 'sent' : 'failed');
    } catch {
      setState('failed');
    }
  };
  return (
    <button type="button" onClick={send} disabled={state === 'sending' || state === 'sent'}
            className={`text-xs underline disabled:no-underline ${className}`}
            style={{ color: 'var(--color-accent-400)' }}
            title="AI is switched off in Vault — ask for it to be enabled">
      {state === 'sent' ? 'Request sent ✓' : state === 'failed' ? 'Could not send — try again'
        : state === 'sending' ? 'Sending…' : 'Ask about enabling AI'}
    </button>
  );
}


/** The AI permission this account has given (Apple 5.1.1(i)). Read from
 * /auth/me, never inferred from a 403 — DRF sends only `detail`. */
export function useAIConsent() {
  const { me, reload } = useAuth() || {};
  const required = me?.ai_consent_required === true;
  const allow = async (yes) => {
    await fetch('/api/ai/consent/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(tokens.access ? { Authorization: `Bearer ${tokens.access}` } : {}),
      },
      body: JSON.stringify({ allow: !!yes }),
    });
    if (reload) await reload();
  };
  return { required, allow };
}

/** What is sent, and to whom — the same words as the privacy policy, shown
 * before anything leaves the device. Used by the sheet and by Account. */
export function AIDisclosure() {
  return (
    <div className="text-sm space-y-3" style={{ color: 'var(--color-slate-300)' }}>
      <p>To read receipts, statements, voice notes and typed spend lines, Vault sends
        what you give it to two AI services:</p>
      <ul className="space-y-2 list-disc pl-5">
        <li><strong>Google Gemini</strong> — receipt and statement images and text,
          voice recordings, the spend line you type, and your expense and category
          names so the result lands on the right expense.</li>
        <li><strong>Anthropic Claude</strong> — the same content when Gemini cannot
          read it, and anything you send through “Report a problem”.</li>
      </ul>
      <p>Your location is included with a receipt only when you have allowed location.</p>
      <p>Neither service may keep your data or train on it, and it is never sold or
        shared with anyone else. You can say no and keep using Vault — every AI
        feature has a manual alternative — and you can withdraw this under Account
        at any time.</p>
    </div>
  );
}

/** The permission sheet. Rendered once, app-wide, and raised by the first AI
 * action; nothing is sent while it is open. */
export function AIConsentSheet({ open, onClose }) {
  const { allow } = useAIConsent();
  const [busy, setBusy] = useState(false);
  if (!open) return null;
  const answer = async (yes) => {
    setBusy(true);
    try { await allow(yes); } finally { setBusy(false); onClose && onClose(yes); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="w-full max-w-lg rounded-2xl p-5 overflow-y-auto"
           style={{ maxHeight: '85vh', background: 'var(--color-slate-900)' }}>
        <h2 className="text-lg font-semibold mb-3" style={{ color: 'var(--color-slate-100)' }}>
          Before Vault reads this with AI
        </h2>
        <AIDisclosure />
        <div className="mt-5 flex gap-3">
          <button type="button" disabled={busy} onClick={() => answer(true)}
                  className="btn-primary flex-1">Allow AI processing</button>
          <button type="button" disabled={busy} onClick={() => answer(false)}
                  className="btn flex-1">Not now</button>
        </div>
      </div>
    </div>
  );
}


// ── One app-wide permission sheet, raised by whichever AI action ran first ──
// ensureAIConsent() resolves true only once the user has allowed it, so an AI
// action can simply stop when it resolves false. Nothing has left the browser
// at that point: the check happens before the request is built.
const CONSENT_EVENT = 'vault:ai-consent-ask';
let pending = [];

export function ensureAIConsent(me) {
  if (me && me.ai_consent_required !== true) return Promise.resolve(true);
  return new Promise((resolve) => {
    pending.push(resolve);
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT));
  });
}

function settle(answer) {
  const waiting = pending;
  pending = [];
  waiting.forEach((r) => r(answer));
  // Not now: say what that means and where to switch it on later.
  if (!answer) window.dispatchEvent(new CustomEvent('vault:ai-blocked', { detail: { kind: 'consent' } }));
}

/**
 * The app-wide "AI couldn't run" notice. Raised by any AI reply that came back
 * blocked (api.js) or by "Not now" on the permission sheet, so every AI
 * feature on web explains itself the same way: why nothing happened, that it
 * still works by hand, and a link straight to the setting that changes it.
 */
export function AIBlockedNotice() {
  const { me, reload } = useAuth() || {};
  const [kind, setKind] = useState(null);      // 'consent' | 'off' | null
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const on = (e) => setKind(e.detail?.kind || 'consent');
    window.addEventListener('vault:ai-blocked', on);
    return () => window.removeEventListener('vault:ai-blocked', on);
  }, []);
  // Allowed meanwhile (the sheet, or Account) — the notice has done its job.
  useEffect(() => {
    if (me?.ai_consent_required === false) setKind((k) => (k === 'consent' ? null : k));
  }, [me?.ai_consent_required]);
  if (!kind) return null;
  const allowNow = async () => {
    setBusy(true);
    try {
      await fetch('/api/ai/consent/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json',
                   ...(tokens.access ? { Authorization: `Bearer ${tokens.access}` } : {}) },
        body: JSON.stringify({ allow: true }),
      });
      if (reload) await reload();
      setKind(null);
    } finally { setBusy(false); }
  };
  const consent = kind === 'consent';
  return (
    <div role="status" aria-live="polite"
         className="fixed z-50 left-1/2 -translate-x-1/2 bottom-5 w-[min(560px,calc(100%-32px))] card !p-4 shadow-2xl border border-vpurple/60">
      <div className="flex items-start gap-3">
        <span className="text-lg" aria-hidden="true">✨</span>
        <div className="flex-1 min-w-0 text-sm">
          <div className="font-semibold">
            {consent ? 'AI is not allowed on your account yet' : 'AI is switched off in Vault right now'}
          </div>
          <p className="text-slate-400 mt-1">
            {consent
              ? 'So this could not be read automatically. You can still do it by hand — or allow AI and try again.'
              : 'So this could not be read automatically. You can still do it by hand.'}
          </p>
          <div className="flex flex-wrap gap-2 mt-3">
            {consent && (
              <button type="button" className="btn-primary !py-1.5 text-sm" disabled={busy} onClick={allowNow}>
                {busy ? 'Allowing…' : 'Allow AI now'}
              </button>
            )}
            {consent ? (
              <Link to="/account#ai-processing" className="border border-edge rounded-xl px-4 text-sm text-slate-200 hover:border-vblue transition py-1.5 inline-flex items-center" onClick={() => setKind(null)}>
                Open AI settings
              </Link>
            ) : me?.is_superuser ? (
              <Link to="/revenue" className="border border-edge rounded-xl px-4 text-sm text-slate-200 hover:border-vblue transition py-1.5 inline-flex items-center" onClick={() => setKind(null)}>
                Open the AI switch
              </Link>
            ) : null}
          </div>
        </div>
        <button type="button" aria-label="Dismiss" className="text-slate-500 hover:text-slate-300 px-1"
                onClick={() => setKind(null)}>✕</button>
      </div>
    </div>
  );
}

/** Mounted once in App.jsx. Owns the sheet and answers every waiting caller. */
export function AIConsentHost() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onAsk = () => setOpen(true);
    window.addEventListener(CONSENT_EVENT, onAsk);
    return () => window.removeEventListener(CONSENT_EVENT, onAsk);
  }, []);
  return (
    <>
      <AIConsentSheet open={open} onClose={(yes) => { setOpen(false); settle(!!yes); }} />
      <AIBlockedNotice />
    </>
  );
}
